import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { AppContextSelector } from "../app-context/app-context.selector";
import { AppError } from "../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../auth/auth.action";
import { AuthSelector } from "../auth/auth.selector";
import { SESSION_ACTION_TYPE } from "../auth/session.action";
import { LoginSuccessfulResponse } from "../auth/session.model";
import { CoreConfig } from "../config/core-config";
import { CUSTOMER_MANAGEMENT_ACTION_TYPE } from "../customer-management/customer-management.action";
import { CustomerManagementSelector } from "../customer-management/customer-management.selector";
import { CoreState } from "../state";

import { JURISDICTION_ACTION_TYPE, JurisdictionAction } from "./jurisdiction.action";
import { JurisdictionSelector } from "./jurisdiction.selector";
import { JurisdictionService } from "./jurisdiction.service";

@Injectable()
export class JurisdictionEffect {
	setJurisdiction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.loginSuccess,
				SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
				SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
				SESSION_ACTION_TYPE.loginWithTokenSuccess
			),
			map((x: ActionWithPayload<LoginSuccessfulResponse>) => x.payload),
			map((payload) => this.action.set(payload.jurisdiction))
		)
	);

	setCustomerJurisdiction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerSuccess),
			concatLatestFrom(() => [
				this.store.select(CustomerManagementSelector.getCustomer),
				this.store.select(JurisdictionSelector.get),
			]),
			filter(
				([, customer, currentJurisdiction]) =>
					!!customer && !!customer.jurisdiction && customer.jurisdiction !== currentJurisdiction
			),
			map(([, customer]) => this.action.set(customer!.jurisdiction!))
		)
	);

	getByCountryCode$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JURISDICTION_ACTION_TYPE.setByCountryCode),
			map((x: ActionWithPayload<string>) => x.payload),
			map((country) => this.service.getJurisdictionByCountryCode(country)),
			map((jurisdiction) => this.action.set(jurisdiction))
		)
	);

	setByAppContext$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JURISDICTION_ACTION_TYPE.setByAppContext, AUTH_ACTION_TYPE.sessionEnd),
			filter(() => !this.coreConfig.isB2b),
			concatLatestFrom(() => [
				this.store.select(AuthSelector.isAuthenticated),
				this.store.select(AppContextSelector.getCountryCode),
			]),
			filter(([, isAuthenticated]) => !isAuthenticated),
			map(([, , countryCode]) => this.service.getJurisdictionByCountryCode(countryCode)),
			concatLatestFrom(() => this.store.select(JurisdictionSelector.get)),
			filter(([jurisdiction, currentJurisdiction]) => jurisdiction !== currentJurisdiction),
			map(([jurisdiction]) => this.action.set(jurisdiction))
		)
	);

	get$ = createEffect(() =>
		this.actions$.pipe(
			ofType(JURISDICTION_ACTION_TYPE.get, SESSION_ACTION_TYPE.loginWithCookieSuccess),
			switchMap(() =>
				this.service.getJurisdiction().pipe(
					map((response) => this.action.getSuccess(response.jurisdiction)),
					catchError((response: AppError) => of(this.action.getFail(response)))
				)
			)
		)
	);

	constructor(
		private actions$: Actions,
		private action: JurisdictionAction,
		private service: JurisdictionService,
		private store: Store<CoreState>,
		private coreConfig: CoreConfig
	) {}
}
